import {
  Anchor,
  Button,
  Card,
  H4,
  H5,
  InputTextArea,
  InputNumber,
} from "@sellar-io/components";
import { Dialog, DialogNavigation, DialogContent } from "../modal/Dialog";
import dayjs from "dayjs";

import { Icon } from "..";
import { twMerge } from "tailwind-merge";
import { useSellarUser } from "../../hooks";
import { InputContainer } from "@sellar-io/components";
import { useState } from "react";
import { useOpeningOrderFeeUpdate } from "../../hooks/api/v2/openingOrderFee/useOpeningOrderFeeUpdate";
import { OpeningOrderFeeType } from "@sellar-io/api-v2/types";
import { useOpeningOrderFeeGet } from "../../hooks/api/v2/openingOrderFee/useOpeningOrderFeeGet";
import { useCreditNoteList } from "../../hooks/api/v2/credit-note/useCreditNoteList";
import { useCreditNoteCreate } from "../../hooks/api/v2/credit-note/useCreditNoteCreate";
import { Alert } from "../alert/Alert";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { formatCurrency, calculateLineItemTotal } from "@sellar-io/common";
import { useCreditNoteDelete } from "../../hooks/api/v2/credit-note/useCreditNoteDelete";
import { AdminOnly } from "../authFilter/AdminOnly";

export const OpeningOrderFeeDialog = ({
  active,
  onDismiss,
  fee,
}: {
  active: boolean;
  onDismiss: () => void;
  fee: OpeningOrderFeeType;
}) => {
  return active ? (
    <OpeningOrderFeeDialogContent
      active={active}
      onDismiss={onDismiss}
      fee={fee}
    />
  ) : null;
};

const OpeningOrderFeeDialogContent = ({
  active,
  onDismiss,
  fee,
}: {
  active: boolean;
  onDismiss: () => void;
  fee: OpeningOrderFeeType;
}) => {
  const user = useSellarUser();
  const { business } = useSellarCurrentBusiness();

  const [waivedReason, setWaivedReason] = useState("");

  const openingOrderFeeQuery = useOpeningOrderFeeGet(fee.id);

  const creditNoteListQuery = useCreditNoteList(
    {
      query: [
        {
          type: "InvoiceLineItemId",
          modifier: "EQUALS",
          value: fee.InvoiceLineItemId ?? 0,
        },
      ],
    },
    {
      enabled: !!fee.InvoiceLineItemId,
    },
  );

  const creditNoteList =
    creditNoteListQuery.data?.pages.flatMap((page) => page) ?? [];

  const totalCredited = creditNoteList.reduce(
    (acc, creditNote) => (acc += creditNote.subtotal),
    0,
  );

  const [creditNoteValue, setCreditNoteValue] = useState(
    fee.fee - totalCredited,
  );
  const [creditNoteDescription, setCreditNoteDescription] = useState(
    `Order ${fee.OrderId} - Credit Note - ${dayjs().format("DD/MM/YYYY")}`,
  );
  const [creditNoteError, setCreditNoteError] = useState<string>();

  const updateOrderFeeMutation = useOpeningOrderFeeUpdate(
    fee.id,
    fee.RetailerId ?? 0,
  );

  const createCreditNoteMutation = useCreditNoteCreate();
  const deleteCreditNoteMutation = useCreditNoteDelete();

  const openingOrderFee = openingOrderFeeQuery.data ?? undefined;

  if (!openingOrderFee) return;

  const rowClasses = "grid grid-cols-2 gap-4 px-4 py-3";
  const openingOrderAmount = openingOrderFee.Order?.requestedPrice
    ? (openingOrderFee.Order.requestedPrice / 1.2 / 100).toFixed(2)
    : (openingOrderFee.fee / 0.15 / 100).toFixed(2);

  const invoiceUrl =
    openingOrderFee.legacyStripeInvoiceUrl ??
    openingOrderFee.InvoiceLineItem?.Invoice.stripeInvoiceUrl;

  const onSubmitWaive = async () => {
    updateOrderFeeMutation.mutate(
      {
        waivedReason,
        waivedAt: dayjs().toDate(),
      },
      {
        onSuccess: () => {
          onDismiss();
        },
      },
    );
  };

  const onSubmitCreditNote = async () => {
    setCreditNoteError(undefined);
    if (totalCredited + creditNoteValue > fee.fee)
      return setCreditNoteError(
        "Total credited amount is greater than the original fee",
      );
    if (!business) return;
    createCreditNoteMutation.mutate(
      {
        type: "openingOrderFees",
        description: creditNoteDescription,
        SenderBusinessId: 1,
        RecipientBusinessId: business.id,
        memo: null,
        subtotal: creditNoteValue,
        total: calculateLineItemTotal(
          { price: creditNoteValue, quantity: 1 },
          20,
        ),
        InvoiceLineItemId: fee.InvoiceLineItemId,
        InvoiceId: null,
        // Historical opening-order fee credit metadata; the fee itself is taxed at 20%.
        vatPercentage: 0,
      },
      {
        onSuccess: () => {
          setCreditNoteValue(fee.fee - totalCredited - creditNoteValue);
        },
        onError: (error) => {
          setCreditNoteError(error.message);
        },
      },
    );
  };

  const onSubmitDeleteCreditNote = async (id: number) => {
    deleteCreditNoteMutation.mutate({
      id,
    });
  };

  return (
    <Dialog
      active={active}
      onDismiss={onDismiss}
      fullHeight={true}
      className={""}
    >
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
        className={"relative z-10 pb-0"}
      >
        <H4 className="pointer-events-none -mt-[58px] text-center">
          <span
            className={twMerge(
              !!openingOrderFee.waivedAt &&
                "font-normal text-gray-500 line-through",
            )}
          >
            Opening order fee - £{(openingOrderFee.fee / 100).toFixed(2)}
          </span>
          {!!openingOrderFee.waivedAt && " FEE WAIVED"}
        </H4>
        <div className="text-center text-gray-500">
          <p>
            As this customer was acquired via the Sellar Market, their first
            order with you is subject to an opening order fee.{" "}
            {!!openingOrderFee.waivedAt
              ? `This fee was waived on ${dayjs(openingOrderFee.waivedAt).format("DD/MM/YYYY")}.`
              : `The fee is
            taken by Sellar independently of the order.`}
          </p>
        </div>
      </DialogNavigation>
      <DialogContent className={"py-6"}>
        <Card
          className={twMerge(
            "bg-white",
            "border",
            !!openingOrderFee.waivedAt && "pointer-events-none opacity-40",
          )}
        >
          <div className={twMerge(rowClasses, "border-b")}>
            <span>Customer</span>
            <span className="font-bold">
              {openingOrderFee.Order?.Retailer.Business.name}
            </span>
          </div>
          <div className={`${rowClasses} border-b`}>
            <span>First order #</span>
            <span className="font-bold">{openingOrderFee.Order?.id}</span>
          </div>
          <div className={`${rowClasses} border-b`}>
            <div>
              <span className="block font-bold">Fee amount</span>
              <span className="text-gray-500">
                15% of first order total ex. VAT
              </span>
            </div>
            <div>
              <span className="block font-bold">
                £{(openingOrderFee.fee / 100).toFixed(2)}
              </span>
              <span className="text-gray-500">
                15% of £{openingOrderAmount}
              </span>
            </div>
          </div>
          <div className={`${rowClasses} border-b`}>
            <span>Fee date</span>
            <span className="font-bold">
              {dayjs(openingOrderFee.createdAt).format("DD/MM/YY")}
            </span>
          </div>
          <div className={rowClasses}>
            <span>Sellar invoice</span>
            <span className="font-bold">
              {invoiceUrl ? (
                <Anchor
                  href={invoiceUrl}
                  target="_blank"
                  className="flex items-center uppercase no-underline"
                >
                  <Icon icon="OPEN_IN_NEW" className="mr-1" />
                  View invoice
                </Anchor>
              ) : (
                `Available on: ${dayjs(openingOrderFee.createdAt).day(6).format("DD/MM/YY")}`
              )}
            </span>
          </div>
        </Card>
        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
          <Card className="flex flex-col border bg-white p-4">
            <H5>About opening order fees</H5>
            <p>
              Find out more about what an opening order fee is using the link
              below.
            </p>
            <Anchor
              href="https://sellar.io/pricing#faq"
              target="_blank"
              className="mt-auto mb-3 flex items-center font-bold uppercase no-underline"
            >
              <Icon icon="OPEN_IN_NEW" className="mr-1" />
              Learn more
            </Anchor>
          </Card>
          <Card className="border bg-white p-4">
            <H5>
              {!!openingOrderFee.waivedAt
                ? "Fee waived"
                : "Issue with this fee?"}
            </H5>
            {!!openingOrderFee.waivedAt ? (
              <p>
                This fee was waived on{" "}
                {dayjs(openingOrderFee.waivedAt).format("DD/MM/YYYY")}. Credit
                will be applied against future fees if you have already been
                billed for this customer.
              </p>
            ) : (
              <p>
                If something looks wrong, use the button below to fill out a
                short form to raise an issue.
              </p>
            )}
            <Button
              color="secondary"
              className="mt-4 flex items-center font-bold uppercase no-underline"
              disabled={!!openingOrderFee.waivedAt}
              onClick={() =>
                window.open(
                  encodeURI(
                    `https://sellar.typeform.com/to/xdQjqeXa#supplier_id=${openingOrderFee.SupplierId ?? "xxxxx"}&supplier_name=${openingOrderFee.Order?.Supplier.Business.name ?? "xxxxx"}&order_id=${openingOrderFee.Order?.id ?? "xxxxx"}&retailer_name=${openingOrderFee.Order?.Retailer.Business.name ?? "xxxxx"}&retailer_id=${openingOrderFee.Order?.Retailer.id ?? "xxxxx"}&fee_amount=£${((openingOrderFee.fee ?? 1) / 100).toFixed(2)}&order_total=£${((openingOrderFee.Order?.requestedPrice ?? openingOrderFee.Order?.price ?? 1) / 100).toFixed(2)}&order_date=${dayjs(openingOrderFee.createdAt).format("DD/MM/YY")}&user_email=${user.user?.email ?? "xxxxx"}`,
                  ),
                )
              }
            >
              <Icon icon="OPEN_IN_NEW" className="mr-1" />
              <span>Raise issue</span>
            </Button>
          </Card>
        </div>
        <AdminOnly>
          {!openingOrderFee.waivedAt && (
            <Card className="mt-4 flex flex-col gap-4 rounded-lg border border-red-500 bg-white p-4">
              {creditNoteList.length === 0 && (
                <>
                  <H4>Waive fee?</H4>
                  <p className="text-sm">
                    Waiving a fee that has already been billed will
                    automatically generate a credit note to the full amount of
                    the fee. This will be used against the next{" "}
                    <b>Opening Order Fee</b> invoice.
                  </p>
                  <div className="flex flex-col gap-2">
                    <span className="mr-auto inline-block text-sm">
                      Enter reason
                    </span>
                    <InputContainer className="py-2 shadow-[0_2px_2px_0_rgba(0,0,0,0.15)]">
                      <InputTextArea
                        variant="legacyFixed"
                        onChange={(e) => {
                          setWaivedReason(e.target.value);
                        }}
                        className="h-[100px] text-sm font-medium"
                      ></InputTextArea>
                    </InputContainer>
                  </div>
                  <Button
                    disabled={waivedReason === ""}
                    onClick={onSubmitWaive}
                    loading={updateOrderFeeMutation.isPending}
                  >
                    Waive fee
                  </Button>
                </>
              )}
              {fee.fee - totalCredited > 0 && !!fee.InvoiceLineItemId && (
                <>
                  {creditNoteList.length === 0 && <hr />}
                  <H4>Raise a credit note?</H4>
                  <p className="text-sm">
                    Raise a credit note up to the value of the fee to reduce the
                    next invoice.
                  </p>
                  {creditNoteError && (
                    <Alert
                      type="error"
                      title={"Check your inputs and try again"}
                      message={creditNoteError}
                    />
                  )}
                  <div className="flex flex-col gap-2">
                    <span className="mr-auto inline-block text-sm">
                      Enter amount in pence/p eg. for £10.00 enter 1000
                    </span>
                    <InputContainer className="py-2 shadow-[0_2px_2px_0_rgba(0,0,0,0.15)]">
                      <InputNumber
                        onChange={(e) =>
                          setCreditNoteValue(Number.parseInt(e.target.value))
                        }
                        value={creditNoteValue}
                        max={fee.fee - totalCredited}
                      />
                    </InputContainer>
                  </div>
                  <div className="flex flex-col gap-2">
                    <span className="mr-auto inline-block text-sm">
                      Enter description
                    </span>
                    <InputContainer className="py-2 shadow-[0_2px_2px_0_rgba(0,0,0,0.15)]">
                      <InputTextArea
                        variant="legacyFixed"
                        onChange={(e) => {
                          setCreditNoteDescription(e.target.value);
                        }}
                        className="h-[100px] text-sm font-medium"
                      >
                        {creditNoteDescription}
                      </InputTextArea>
                    </InputContainer>
                  </div>
                  <Button
                    onClick={onSubmitCreditNote}
                    loading={updateOrderFeeMutation.isPending}
                  >
                    Raise credit note
                  </Button>
                </>
              )}
              {creditNoteList.length > 0 && (
                <>
                  <hr />
                  <H4>Raised credit notes</H4>
                  <div className="flex flex-col gap-2">
                    {creditNoteList.map((creditNote, i) => (
                      <div
                        key={i}
                        className="flex flex-wrap items-center justify-between rounded-lg border border-zinc-300 bg-zinc-50 p-2"
                      >
                        <span>
                          <b>Description:</b> {creditNote.description}
                        </span>
                        <span>
                          <b>Subtotal:</b> {formatCurrency(creditNote.subtotal)}
                        </span>
                        {!creditNote.InvoiceId && (
                          <Button
                            color="red"
                            size="small"
                            onClick={() =>
                              onSubmitDeleteCreditNote(creditNote.id)
                            }
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </Card>
          )}
        </AdminOnly>
      </DialogContent>
    </Dialog>
  );
};
